import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { IconButton, Stack, Typography, Box, Tooltip } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { neutral, error } from '_lex/_foundations/phxColors';

export type DetailListItem = {
  key?: string;
  label: React.ReactNode;
  value?: React.ReactNode;
  copyable?: boolean;
  copyValue?: string;
  truncate?: boolean;
  truncateLines?: number;
};

export type DetailListProps = {
  items: DetailListItem[];
  columns?: number;
  highlightNulls?: boolean;
};

function DetailList({ items, columns, highlightNulls = false }: DetailListProps) {
  // If columns is provided, use it (max 4), otherwise default to responsive up to 3
  const maxColumns = columns ? Math.min(columns, 4) : 3;
  const isResponsive = !columns;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 4,
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: isResponsive ? 'repeat(2, 1fr)' : `repeat(${Math.min(maxColumns, 2)}, 1fr)`,
          md: isResponsive ? 'repeat(3, 1fr)' : `repeat(${Math.min(maxColumns, 3)}, 1fr)`,
          lg: `repeat(${maxColumns}, 1fr)`,
        },
      }}
    >
      {items.map((item, ind) => {
        const hasStringValue = typeof item.value === 'string';
        const shouldTruncate = Boolean(item.truncate && hasStringValue);
        const lineClamp = item.truncateLines ?? 3;

        const valueContent = (
          <Typography
            variant='bodySm'
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              color: neutral[500],
              whiteSpace: shouldTruncate ? 'normal' : 'pre-wrap',
              display: shouldTruncate ? '-webkit-box' : 'block',
              WebkitLineClamp: shouldTruncate ? lineClamp : undefined,
              WebkitBoxOrient: shouldTruncate ? 'vertical' : undefined,
              overflow: shouldTruncate ? 'hidden' : undefined,
              textOverflow: shouldTruncate ? 'ellipsis' : undefined,
              '& small': {
                fontWeight: 'normal',
                color: neutral[500],
                ml: 2,
              },
            }}
          >
            {item.value ?? '--'}
          </Typography>
        );

        const valueNode = (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {valueContent}
          </Box>
        );

        return (
          <Box
            key={`item_${ind}`}
            className={`item-${item.key ?? ind.toString()}`}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Stack spacing={1}>
              <Typography
                variant='subtitleSm'
                sx={{
                  color: highlightNulls && !item.value ? error[600] : neutral[700],
                }}
              >
                {item.label}
              </Typography>
              <Stack
                direction='row'
                spacing={1}
                sx={{ alignItems: 'center', minWidth: 0, flexWrap: 'nowrap' }}
              >
                {shouldTruncate ? (
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          maxWidth: 360,
                          maxHeight: 320,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          p: 2,
                          color: 'inherit',
                        }}
                      >
                        <Typography
                          variant='bodySm'
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {item.value as string}
                        </Typography>
                      </Box>
                    }
                    placement='top-start'
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          pointerEvents: 'auto',
                          p: 0,
                        },
                      },
                      popper: {
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            options: {
                              padding: 8,
                            },
                          },
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 12],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    {valueNode}
                  </Tooltip>
                ) : (
                  valueNode
                )}
                {item.copyable && item.copyValue && (
                  <CopyToClipboard text={item.copyValue}>
                    <IconButton>
                      <ContentCopy />
                    </IconButton>
                  </CopyToClipboard>
                )}
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

export default DetailList;
